import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Music, Calendar, Play, Users } from 'lucide-react';
import VideoSongCard from './VideoSongCard';

// Local Song interface that matches the component's usage
interface Song {
  id: string;
  title: string;
  artist: string;
  duration?: string; // This component expects string format like "3:45"
  year?: number;
  genre?: string;
  albumCover?: string;
  album_cover_url?: string; // Database field name
  videoUrl?: string;
}

interface PublicSongsTabProps {
  userSongs: Record<number, Song[]>;
  profileUser: any; // User whose profile we're viewing
  totalSongs: number;
}

const PublicSongsTab: React.FC<PublicSongsTabProps> = ({ 
  userSongs, 
  profileUser, 
  totalSongs 
}) => {
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const years = Object.keys(userSongs).sort((a, b) => Number(b) - Number(a));

  // Filter songs to only show those with videos for public view
  const songsWithVideos = Object.entries(userSongs).reduce((acc, [year, songs]) => {
    const videosOnlySongs = songs.filter(song => song.videoUrl && song.videoUrl.trim() !== '');
    if (videosOnlySongs.length > 0) {
      acc[Number(year)] = videosOnlySongs;
    }
    return acc;
  }, {} as Record<number, Song[]>);

  const yearsWithVideos = Object.keys(songsWithVideos).sort((a, b) => Number(b) - Number(a));
  const totalVideos = Object.values(songsWithVideos).flat().length;

  const handleVideoPlay = (songId: string) => {
    // Stop any currently playing video and start the new one
    setCurrentlyPlaying(currentlyPlaying === songId ? null : songId);
  };

  if (yearsWithVideos.length === 0) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Music className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg text-blue-900">
                  {profileUser?.user_metadata?.full_name || 'User'}'s Light Show Videos
                </CardTitle>
                <p className="text-sm text-blue-700 mt-1">
                  Discover amazing holiday light shows set to music
                </p>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* No videos message */}
        <Card>
          <CardContent className="py-12">
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                <Play className="h-6 w-6 text-gray-400" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No videos available yet</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  {profileUser?.user_metadata?.full_name || 'This user'} hasn't shared any light show videos yet. 
                  Check back later to see their amazing displays!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Music className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg text-blue-900">
                {profileUser?.user_metadata?.full_name || 'User'}'s Light Show Videos
              </CardTitle>
              <p className="text-sm text-blue-700 mt-1">
                {totalVideos} video{totalVideos !== 1 ? 's' : ''} showcasing amazing holiday displays
              </p>
            </div>
            <Badge variant="secondary" className="bg-blue-100 text-blue-800">
              <Users className="w-3 h-3 mr-1" />
              {totalVideos} Videos
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-blue-100">
              <Play className="h-4 w-4 text-green-500" />
              <div>
                <p className="font-medium text-sm text-gray-900">Watch & Enjoy</p>
                <p className="text-xs text-gray-600">Experience the magic of synchronized light shows</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-blue-100">
              <Music className="h-4 w-4 text-purple-500" />
              <div>
                <p className="font-medium text-sm text-gray-900">Get Inspired</p>
                <p className="text-xs text-gray-600">Discover new songs for your own display</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Videos by Year */}
      {yearsWithVideos.map(year => (
        <Card key={year}>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-blue-600" />
              <CardTitle className="text-xl">{year}</CardTitle>
              <Badge variant="outline">
                {songsWithVideos[Number(year)].length} video{songsWithVideos[Number(year)].length !== 1 ? 's' : ''}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {songsWithVideos[Number(year)].map((song) => (
                <VideoSongCard
                  key={song.id}
                  song={song}
                  isPlaying={currentlyPlaying === song.id}
                  onPlay={() => handleVideoPlay(song.id)}
                  className="h-full"
                />
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Footer info */}
      {totalVideos > 3 && (
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="py-4">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>
                Showing {totalVideos} video{totalVideos !== 1 ? 's' : ''} from {yearsWithVideos.length} year{yearsWithVideos.length !== 1 ? 's' : ''}
              </span>
              <span>
                Powered by react-player
              </span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PublicSongsTab; 